export const getColor = (change: number): string => {
  if (change >= 5) return "#0d5c2e";
  if (change >= 3) return "#166634";
  if (change >= 1.5) return "#15803d";
  if (change >= 0.5) return "#16a34a";
  if (change > 0) return "#22c55e";
  if (change === 0) return "#374151";
  if (change >= -0.5) return "#b91c1c";
  if (change >= -1.5) return "#991b1b";
  if (change >= -3) return "#7f1d1d";
  if (change >= -10) return "#6b1a1a";
  return "#450a0a";
};

export const fmt = (n?: number, decimals = 2) => {
  if (n === undefined || n === null) return "—";
  return Number(n).toFixed(decimals);
};

/* selectHoverBorder / selectOptionSelectedBg / selectOptionSelectedText
   are the only accent-semantic values in either theme object — every
   other field here is a genuinely neutral slate/gray, correctly
   independent of which palette is active. These three used to be
   literal blue hex (#4f6b8a/#1e3a5f/#93c5fd dark, #6b7280/#dbeafe/
   #1d4ed8 light) — a real gap: this object has no awareness of
   data-vi-palette at all, so the Select's focus/selected states always
   read blue regardless of the active palette. Set to the same
   var(--vi-accent)-based value in both objects instead — the CSS
   variable itself already resolves to the correct color for whichever
   mode/palette is active when the browser parses the injected
   stylesheet below, so DARK_THEME/LIGHT_THEME no longer need their own
   copies of these three specifically. */
export const DARK_THEME = {
  bg: "linear-gradient(135deg, color-mix(in srgb, #0f1623 92%, transparent) 0%, color-mix(in srgb, #131b2e 92%, transparent) 100%)",
  border: "#1e293b",
  labelColor: "#94a3b8",
  legendText: "#ffffffcc",
  tooltipBg: "#1a1f2e",
  tooltipBorder: "#2d3748",
  tooltipText: "#e2e8f0",
  tooltipMuted: "#94a3b8",
  divider: "#2d3748",
  unchangedPill: "#4b5563",
  selectLabelColor: "#64748b",
  // The closed control (Market/Sector <Select>) stays flat/card-like,
  // same convention every other page's Select uses — only the open
  // dropdown popup below gets the glass treatment. Token-driven rather
  // than a hand-duplicated literal hex, so it tracks the shared card
  // color (and any future palette change) instead of drifting from it.
  selectBg: "var(--vi-card-bg)",
  selectBorder: "var(--vi-card-border)",
  selectText: "var(--vi-text)",
  selectHoverBorder: "var(--vi-accent)",
  // Dropdown popup — was a flat literal hex with a plain drop shadow
  // and no blur at all, unlike every other themed page's Select popup
  // (--vi-overlay-* + backdrop-filter). Now the same overlay recipe;
  // see injectSelectStyles below for where the blur/border/shadow are
  // actually applied (this token only covers item backgrounds here).
  selectOptionBg: "var(--vi-overlay-bg)",
  selectOptionHoverBg: "var(--vi-surface-hover)",
  selectOptionSelectedBg: "color-mix(in srgb, var(--vi-accent) 16%, transparent)",
  selectOptionSelectedText: "var(--vi-accent)",
};

export const LIGHT_THEME = {
  // Was a plain neutral gray gradient with no accent identity at all —
  // a flat slate wash, not "glassy" the way every other themed page's
  // main panel now reads. A diagonal accent-tinted sheen layered on top
  // (same recipe those pages' `.vi-glass-card` light-mode override
  // uses) instead of just deepening the neutral tone directly — keeps
  // the heat-tile grid's own contrast/readability intact underneath.
  bg: "linear-gradient(135deg, color-mix(in srgb, var(--vi-accent) 10%, transparent) 0%, transparent 45%), linear-gradient(135deg, color-mix(in srgb, #f8fafc 92%, transparent) 0%, color-mix(in srgb, #f1f5f9 92%, transparent) 100%)",
  border: "color-mix(in srgb, var(--vi-accent) 22%, #e2e8f0)",
  labelColor: "#64748b",
  legendText: "#ffffffcc",
  tooltipBg: "#ffffff",
  tooltipBorder: "#e2e8f0",
  tooltipText: "#1e293b",
  tooltipMuted: "#64748b",
  divider: "#e2e8f0",
  unchangedPill: "#94a3b8",
  selectLabelColor: "#6b7280",
  // Same token references as DARK_THEME's copy of these five fields
  // (see its comment) — var(--vi-card-bg) etc. already resolve to the
  // correct light-mode value on their own, so this is intentionally
  // identical rather than a second hand-maintained hex set.
  selectBg: "var(--vi-card-bg)",
  selectBorder: "var(--vi-card-border)",
  selectText: "var(--vi-text)",
  selectHoverBorder: "var(--vi-accent)",
  selectOptionBg: "var(--vi-overlay-bg)",
  selectOptionHoverBg: "var(--vi-surface-hover)",
  selectOptionSelectedBg: "color-mix(in srgb, var(--vi-accent) 16%, transparent)",
  selectOptionSelectedText: "var(--vi-accent)",
};

export type HeatmapTheme = typeof DARK_THEME;

export const STYLE_TAG_ID = "heatmap-select-overrides";
export const SCOPE_ID = "hm1";

export const injectSelectStyles = (isDark: boolean, scopeId: string) => {
  const t = isDark ? DARK_THEME : LIGHT_THEME;
  const scope = `[data-heatmap-scope="${scopeId}"]`;

  // Dropdown panel fill — same "surface-tier" glass recipe as every
  // other page's stock-search/upgraded popup (.vi-fin-stock-popup, .vi-
  // dg-stock-popup, .vi-scr-glass-popup, …), on request, but at a much
  // higher opacity than those. Every one of those sits over a plain
  // page background or ordinary table rows; this dropdown opens
  // directly on top of the heat-tile grid itself — saturated red/green
  // blocks, not a quiet background — so the same ~35%/3% fill those
  // pages use let the tiles' own colors bleed through legibly enough to
  // make the option text hard to read (reported: "not that much visible
  // clearly"). Near-opaque here instead; the blur/accent-tint/border/
  // glow still carry the "glass" identity, translucency just isn't
  // doing any of that work in this specific spot. This page has no
  // static CSS class to hang the recipe on (the whole popup is built
  // from this JS template + `isDark`), so the two values that recipe
  // normally switches per-theme via `:root.dark .vi-*-stock-popup {
  // --vi-*-stock-popup-bg: ... }` are just picked directly here instead,
  // since `isDark` is already known at generation time.
  const popupBg = isDark
    ? "rgba(15, 20, 32, 0.96)"
    : "color-mix(in srgb, var(--vi-accent) 8%, rgba(255, 255, 255, 0.94))";

  const css = `
    ${scope} .ant-select .ant-select-selector {
      background-color: ${t.selectBg} !important;
      border-color: ${t.selectBorder} !important;
      color: ${t.selectText} !important;
      transition: background-color 0.3s, border-color 0.2s !important;
    }
    ${scope} .ant-select:hover .ant-select-selector {
      border-color: ${t.selectHoverBorder} !important;
    }
    ${scope} .ant-select-focused .ant-select-selector {
      border-color: ${t.selectHoverBorder} !important;
      box-shadow: 0 0 0 2px color-mix(in srgb, ${t.selectHoverBorder} 20%, transparent) !important;
    }
    ${scope} .ant-select .ant-select-arrow { color: ${
    t.selectLabelColor
  } !important; }
    ${scope} .ant-select .ant-select-selection-item { color: ${
    t.selectText
  } !important; }
    ${scope} .ant-select .ant-select-selection-placeholder { color: ${
    t.selectLabelColor
  } !important; }
    .heatmap-select-dropdown-${scopeId} {
      /* Was the shared --vi-overlay-* recipe (a flat literal-hex panel
         before that, further back) — now the same airy, accent-tinted
         glass every other page's dropdown got upgraded to (see the
         popupBg comment above): a diagonal sheen, an accent-tinted
         border, and an accent glow in the shadow, instead of the
         heavier neutral overlay recipe. */
      background:
        linear-gradient(135deg, rgba(255, 255, 255, 0.22) 0%, rgba(255, 255, 255, 0) 45%),
        ${popupBg} !important;
      border: 1px solid color-mix(in srgb, var(--vi-accent) 30%, transparent) !important;
      border-radius: var(--vi-glass-surface-radius) !important;
      box-shadow: 0 16px 40px rgba(15, 23, 42, 0.14),
        0 0 0 1px rgba(255, 255, 255, 0.4) inset,
        0 0 24px color-mix(in srgb, var(--vi-accent) 16%, transparent) !important;
      backdrop-filter: blur(30px) saturate(170%);
      -webkit-backdrop-filter: blur(30px) saturate(170%);
      overflow: hidden;
    }
    .heatmap-select-dropdown-${scopeId} .ant-select-item {
      color: ${t.selectText} !important;
      background-color: transparent !important;
      font-size: 13px !important;
    }
    .heatmap-select-dropdown-${scopeId} .ant-select-item:hover,
    .heatmap-select-dropdown-${scopeId} .ant-select-item-option-active {
      background-color: ${t.selectOptionHoverBg} !important;
    }
    .heatmap-select-dropdown-${scopeId} .ant-select-item-option-selected {
      background-color: ${t.selectOptionSelectedBg} !important;
      color: ${t.selectOptionSelectedText} !important;
      font-weight: 600 !important;
    }
  `;

  let tag = document.getElementById(STYLE_TAG_ID);
  if (!tag) {
    tag = document.createElement("style");
    tag.id = STYLE_TAG_ID;
    document.head.appendChild(tag);
  }
  tag.textContent = css;
};
